/*
 * Copyright (c) 2020 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import { CashBoxListItemDto, CashBoxesListFilterParams } from '../../api/cashBoxes/cashBoxesClient.typings';
import { CashBoxesAction, CashBoxesActionType } from '../../actions/CashBoxRegistry/cashBoxesActions';
import { CashBoxeState, CashBoxesStatus } from '../../api/cashBoxes/cashBoxesClient.typings';
import { CashBoxesListTableData } from '../../../components/RegistryWidgets/CashBoxes/CashBoxesList/CashBoxesListConfig';
import { Order } from '../../api/apiCommon.typings';
import { getPageLimitByScreenSize, sortVersions } from '../../../utils/utils';
import { SoftwareVersionItemDto } from '../../api/cashRegisterModels/cashRegisterModelsClient.typings';

export const defaultSortingKey: keyof CashBoxesListTableData = 'taxNumber';
export const defaultSortingOrder: Order = 'asc';
export const defaultSearchType: keyof CashBoxesListTableData = 'taxNumber';
export const defaultCashBoxStateFilter: CashBoxeState = null;
export const defaultCashBoxTypeFilter: boolean = null;
export const defaultGpsTrackingFilter: boolean = null;
export const defaultPortableFilter: boolean = null;
export const defaultCashBoxesStatusesFilter: CashBoxesStatus[] = [
    'REGISTRATION',
    'ACTIVE',
    'BLOCKED',
    'RESET',
    'BROKEN',
    'SERVICE',
];

export interface CashBoxesState {
    cashboxesList: CashBoxListItemDto[];
    softwareVersionsList: SoftwareVersionItemDto[];
    limit: number;
    afterKeyTaxNumber: string;
    lastRequestItemsCount: number;
    filterParams: CashBoxesListFilterParams;
    changedFilterParams: CashBoxesListFilterParams;
    isLoading: boolean;
}

export const initialState: CashBoxesState = {
    cashboxesList: [],
    softwareVersionsList: [],
    limit: getPageLimitByScreenSize(),
    afterKeyTaxNumber: null,
    lastRequestItemsCount: null,
    filterParams: {
        search_type: defaultSearchType,
        search_text: '',
        state: defaultCashBoxStateFilter,
        temporary: defaultCashBoxTypeFilter,
        gpsTracking: defaultGpsTrackingFilter,
        portable: defaultPortableFilter,
        cashBoxStatuses: [...defaultCashBoxesStatusesFilter],
        cashBoxTypes: [],
        storeTypes: [],
        cashRegisterModelTaxNumbers: [],
        operatorTaxNumbers: [],
        firmwareVersions: [],
        fiscalDriverVersions: [],
        taxationTypes: [],
        storeTaxAuthorityIds: [],
        fromOffline: null,
        toOffline: null,
        fromLastDocument: null,
        toLastDocument: null,
        fromTimeDelta: null,
        toTimeDelta: null,
        fromExpiredCert: null,
        toExpiredCert: null,
        fromRegisteredAtUtc: null,
        toRegisteredAtUtc: null,
        storeMainActivities: [],
        storeMiddleActivities: [],
        storeLowerActivities: [],
        softwareVersionStatuses: [],
        softwareVersions: [],
        regionCodes: [],
    },
    changedFilterParams: null,
    isLoading: false,
};

export function cashBoxesReducer(state: CashBoxesState = initialState, action: CashBoxesAction): CashBoxesState {
    switch (action.type) {
        case CashBoxesActionType.OPEN_REGISTRY_CASHBOXES_LIST_PAGE:
            return {
                ...state,
            };

        case CashBoxesActionType.FETCH_NEXT_PAGE_START:
            return {
                ...state,
                isLoading: true,
                changedFilterParams: null,
            };

        case CashBoxesActionType.APPLY_FILTERS:
            return {
                ...state,
                isLoading: true,

                cashboxesList: [],
                afterKeyTaxNumber: null,
                lastRequestItemsCount: null,

                filterParams: state.changedFilterParams ? { ...state.changedFilterParams } : { ...state.filterParams },
                changedFilterParams: null,
            };

        case CashBoxesActionType.APPLY_FILTERS_FROM_OUTSIDE_SCREEN:
            return {
                ...state,

                cashboxesList: [],
                afterKeyTaxNumber: null,
                lastRequestItemsCount: null,

                filterParams: action.payload,
                changedFilterParams: null,
            };

        case CashBoxesActionType.CHANGE_FILTERS:
            return {
                ...state,
                changedFilterParams: action.payload,
            };

        case CashBoxesActionType.FETCH_CASHBOXES_LIST_PAGE_SUCCESS:
            const combinedList: CashBoxListItemDto[] = [...state.cashboxesList];
            if (action.payload.list.cashBoxes && action.payload.list.cashBoxes.length > 0) {
                combinedList.push(...action.payload.list.cashBoxes);
            }
            return {
                ...state,
                cashboxesList: combinedList,
                afterKeyTaxNumber: action.payload.list.afterKeyTaxNumber,
                lastRequestItemsCount: action.payload.list.cashBoxes.length,
                isLoading: false,
            };

        case CashBoxesActionType.FETCH_CASHBOXES_LIST_PAGE_ERROR:
            return {
                ...state,
                isLoading: false,
            };

        case CashBoxesActionType.DOWNLOAD_EXCEL:
            return {
                ...state,
                isLoading: true,
                changedFilterParams: null,
            };

        case CashBoxesActionType.DOWNLOAD_EXCEL_SUCCESS:
        case CashBoxesActionType.DOWNLOAD_EXCEL_ERROR:
            return {
                ...state,
                isLoading: false,
            };

        case CashBoxesActionType.FETCH_CASH_REGISTERS_VERSIONS_LIST_SUCCESS:
            const sortedSoftwareVersions = sortVersions<SoftwareVersionItemDto>(action.payload, 'name', 'desc');
            return {
                ...state,
                softwareVersionsList: sortedSoftwareVersions,
            };

        case CashBoxesActionType.EXIT_REGISTRY_CASHBOXES_LIST_PAGE:
            return {
                ...state,
                changedFilterParams: null,
            };

        default:
            return state;
    }
}
